/**
 * JWT helpers.
 *
 * Split out of AuthService so the guard, the layout and the services all read claims the same
 * way — the previous arrangement had two copies of the feature-key walk and one hand-rolled
 * decode that could not handle a base64url payload.
 */

/** Claims the backend adds for company isolation. Must match CompanyClaims on the server. */
export const COMPANY_ID_CLAIM = 'company_id';
export const SCOPE_CLAIM = 'scope';
export const TOKEN_VERSION_CLAIM = 'tver';

export const SCOPE_PLATFORM = 'platform';
export const SCOPE_COMPANY = 'company';

export const SUPER_ADMIN_ROLE = 'SuperAdmin';

export type JwtPayload = {
  exp?: number;
  [claim: string]: unknown;
};

/**
 * Decodes a JWT payload.
 *
 * JWTs are base64**url** encoded: '-' and '_' replace '+' and '/', and the padding is stripped.
 * `atob` accepts neither, so the previous implementation threw on any token whose payload
 * happened to contain those characters — intermittently, depending on the claims. Restoring the
 * alphabet and padding first makes it deterministic.
 */
export function decodeJwt(token: string | null | undefined): JwtPayload | null {
  if (!token) return null;

  const segments = token.split('.');
  if (segments.length !== 3) return null;

  try {
    const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

    // decodeURIComponent/escape round-trip so non-ASCII claims survive.
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );

    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string | null | undefined): boolean {
  const payload = decodeJwt(token);
  if (!payload?.exp) return true;

  return payload.exp < Math.floor(Date.now() / 1000);
}

export function companyIdOf(token: string | null | undefined): string | null {
  const value = decodeJwt(token)?.[COMPANY_ID_CLAIM];
  return typeof value === 'string' ? value : null;
}

export function scopeOf(token: string | null | undefined): string | null {
  const value = decodeJwt(token)?.[SCOPE_CLAIM];
  return typeof value === 'string' ? value : null;
}

/** True for a SuperAdmin token: platform scope, not a company one. */
export function isPlatformToken(token: string | null | undefined): boolean {
  return scopeOf(token) === SCOPE_PLATFORM;
}

/**
 * Flattens the nested accessFeatures tree into visualKeys.
 *
 * Previously duplicated verbatim in auth.service.ts and auth.guard.ts, which meant a fix to one
 * silently left the other behind.
 */
export function extractVisualKeys(features: unknown): string[] {
  const keys: string[] = [];
  const stack: any[] = Array.isArray(features) ? [...features] : [];

  while (stack.length) {
    const node = stack.pop();
    if (node && typeof node.visualKey === 'string') {
      keys.push(node.visualKey);
    }
    if (node && Array.isArray(node.children) && node.children.length) {
      stack.push(...node.children);
    }
  }

  return keys;
}
